import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../services/api.ts';
import { formatStatus } from '../utils/statusFormatter.ts';

interface Receivable {
  id: string;
  customerName: string;
  description: string;
  originalAmount: number;
  currentAmount: number;
  interestRate: number;
  dueDate: string;
  status: 'active' | 'paid' | 'overdue' | 'renegotiated';
  category: 'real_estate' | 'vehicle' | 'mutuos' | 'other';
  paymentMethod?: string;
  installments?: number;
  currentInstallment?: number;
  createdAt: string;
  updatedAt: string;
}

const ReceivableDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: receivable,
    isLoading,
    error,
  } = useQuery<Receivable>(
    ['receivable', id],
    async () => {
      const response = await api.get(`/receivables/${id}`);
      return response.data;
    },
    { enabled: !!id }
  );

  const deleteMutation = useMutation(
    async () => {
      await api.delete(`/receivables/${id}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('receivables');
        navigate('/receivables');
      },
      onError: (error: any) => {
        console.error('Erro ao excluir recebível:', error);
      },
    }
  );

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !receivable) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Recebível não encontrado</h3>
          <p className="text-gray-500 mb-6">
            O recebível solicitado não foi encontrado ou não existe.
          </p>
          <button
            onClick={() => navigate('/receivables')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Voltar para Recebíveis
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{receivable.customerName}</h1>
          <p className="text-gray-600 mt-2">{receivable.description}</p>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Informações do Recebível</h2>
          </div>

          <div className="px-6 py-4 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Valor Original</label>
                <p className="text-lg font-semibold text-gray-900">
                  R$ {receivable.originalAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Saldo Restante</label>
                <p className="text-lg font-semibold text-gray-900">
                  R$ {receivable.currentAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Taxa de Juros</label>
                <p className="text-lg font-semibold text-gray-900">{receivable.interestRate}% ao mês</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Data de Vencimento</label>
                <p className="text-lg font-semibold text-gray-900">
                  {new Date(receivable.dueDate).toLocaleDateString('pt-BR')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Status</label>
                <p className="text-lg font-semibold text-gray-900">{formatStatus(receivable.status)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Categoria</label>
                <p className="text-lg font-semibold text-gray-900">
                  {receivable.category === 'real_estate'
                    ? 'Imóvel'
                    : receivable.category === 'vehicle'
                    ? 'Veículo'
                    : receivable.category === 'mutuos'
                    ? 'Mútuos'
                    : 'Outros'}
                </p>
              </div>
            </div>

            {receivable.paymentMethod && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Método de Recebimento</label>
                <p className="text-lg font-semibold text-gray-900">{receivable.paymentMethod}</p>
              </div>
            )}

            {receivable.installments && receivable.installments > 1 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Parcelas</label>
                  <p className="text-lg font-semibold text-gray-900">
                    {receivable.currentInstallment || 1} de {receivable.installments}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Progresso</label>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{
                        width: `${((receivable.currentInstallment || 1) / receivable.installments) * 100}%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <button
            onClick={() => navigate(`/receivables/${receivable.id}/edit`)}
            className="inline-flex items-center px-4 py-2 border border-primary-200 text-sm font-medium rounded-lg text-primary-700 bg-primary-50 hover:bg-primary-100 transition-colors"
          >
            <PencilIcon className="h-4 w-4 mr-2" /> Editar
          </button>
          <button
            onClick={handleDelete}
            className="inline-flex items-center px-4 py-2 border border-red-200 text-sm font-medium rounded-lg text-red-700 bg-red-50 hover:bg-red-100 transition-colors"
          >
            <TrashIcon className="h-4 w-4 mr-2" /> Excluir
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceivableDetail;